import { EventInput } from '@fullcalendar/core';
import { fCurrency } from '../../utils/formatNumber';
import { formatDateInBrandTZ } from '../../utils/formatTime';
import { ReservationGeneral, ReservationGuestType } from './reservationGeneral';
import { TaskGeneral } from './taskGeneral';

export enum CalendarReservationContentMode {
  All = 'all',
  SingleListing = 'single-listing'
}

const calendarDatePattern = /^(\d{4}-\d{2}-\d{2})/;

function toCalendarDate(value: string | null | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  return value.match(calendarDatePattern)?.[1];
}

class CalendarEvent implements EventInput {
  constructor(
    public readonly id: string,
    protected _start: Date | string | undefined,
    protected _end: Date | string | undefined,
    protected _allDay: boolean = true
  ) {}

  get start() {
    return this._start;
  }
  get end() {
    return this._end;
  }
  get allDay() {
    return this._allDay;
  }
}

export class CalendarReservation extends CalendarEvent {
  public readonly title: string;
  public readonly className: string;

  constructor(
    public readonly model: ReservationGeneral,
    public readonly color: string,
    public readonly textColor: string,
    public readonly contentMode: CalendarReservationContentMode
  ) {
    super(model.id, formatDateInBrandTZ(model.check_in, true), formatDateInBrandTZ(model.check_out, true), false);
    this.title = CalendarReservation.createTitle(contentMode, model);
    this.className = CalendarReservation.createClassName(model);
    if (contentMode === CalendarReservationContentMode.SingleListing) {
      this.color = '#00AB55';
      this.textColor = '#00AB55';
    }
  }
  static createTitle(mode: CalendarReservationContentMode, reservation_model: ReservationGeneral) {
    let title = '';

    // Add type prefix for special blocks
    if (reservation_model.type === 'Housekeeping') {
      title = 'Housekeeping';
    } else if (reservation_model.type === 'Maintenance') {
      title = 'Maintenance';
    } else if (mode === CalendarReservationContentMode.SingleListing) {
      title = reservation_model.ui_table_guest_name;
      if (reservation_model.name) {
        title += ` - ${reservation_model.name}`;
      }
    } else {
      title = reservation_model.unit_id;
      if (reservation_model.ui_table_guest_name) {
        title += ` - ${reservation_model.ui_table_guest_name}`;
      }
    }

    // Only add rental rate for guest reservations
    if (reservation_model.type === ReservationGuestType.Guest) {
      title += ` - ${fCurrency(reservation_model.rental_amount)}`;
    }

    return title;
  }

  static createClassName(reservation_model: ReservationGeneral) {
    const baseClass = 'calendar-reservation';

    if (reservation_model.type === 'Housekeeping') {
      return `${baseClass} calendar-housekeeping`;
    }
    if (reservation_model.type === 'Maintenance') {
      return `${baseClass} calendar-maintenance`;
    }
    return baseClass;
  }
}

export class CalendarTasks extends CalendarEvent {
  constructor(
    public readonly title: string,
    start: Date | undefined,
    end: Date | undefined,
    public readonly model: TaskGeneral,
    public readonly color: string,
    public readonly textColor: string,
    public readonly contentMode: CalendarReservationContentMode
  ) {
    super(model.id, start, end, false);

    if (!model.completed_time && !model.start_time && !model.dueDate) {
      console.warn('Undisplayable task event model', model);
    }

    if (model.start_time && model.completed_time) {
      this._start = model.start_time;
      this._end = model.completed_time;
      this._allDay = false;
    } else if (model.completed_time) {
      this._start = model.completed_time;
      this._end = model.completed_time;
      this._allDay = false;
    } else if (model.dueDate) {
      const dueDate = toCalendarDate(model.due_date);
      this._start = dueDate;
      this._end = undefined;
      this._allDay = true;
    }
  }
}
